if (!window.KexTieredDiscount) {
  class KexTieredDiscountManager {
    constructor(script) {
      this.script = script;
      this.codeTwo = script?.dataset.discountTwo || 'JUNTOS3';
      this.codeThree = script?.dataset.discountThree || 'JUNTOS5';
      this.percentTwo = Number(script?.dataset.percentTwo || 3);
      this.percentThree = Number(script?.dataset.percentThree || 5);
      this.statusTwo = script?.dataset.statusTwo || '';
      this.statusThree = script?.dataset.statusThree || '';
      this.storageKey = 'kex-tiered-discount-active';
      this.queue = Promise.resolve();
      this.lastResult = null;
      this.boundSchedule = this.schedule.bind(this);
      this.bindEvents();

      if (this.isActive()) this.schedule();
    }

    root() {
      return window.Shopify?.routes?.root || '/';
    }

    isActive() {
      try {
        return window.localStorage.getItem(this.storageKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    setActive(active) {
      try {
        if (active) {
          window.localStorage.setItem(this.storageKey, 'true');
        } else {
          window.localStorage.removeItem(this.storageKey);
        }
      } catch (error) {
        return;
      }
    }

    bindEvents() {
      document.addEventListener('on:cart:add', this.boundSchedule);
      document.addEventListener('on:line-item:change', this.boundSchedule);
      document.addEventListener('on:quick-order-list:update', this.boundSchedule);
      document.addEventListener('dispatch:cart-drawer:refresh', this.boundSchedule);
      window.addEventListener('pageshow', () => {
        if (this.isActive()) this.schedule();
      });
    }

    distinctProductCount(cart) {
      const productIds = new Set(
        (cart?.items || [])
          .filter((item) => Number(item.quantity) > 0)
          .map((item) => String(item.product_id))
      );
      return productIds.size;
    }

    tierForCount(count) {
      if (count >= 3) {
        return { code: this.codeThree, percent: this.percentThree, count };
      }
      if (count === 2) {
        return { code: this.codeTwo, percent: this.percentTwo, count };
      }
      return { code: '', percent: 0, count };
    }

    manualCodes(cart) {
      const cartApplications = cart?.cart_level_discount_applications || [];
      const lineApplications = (cart?.items || []).flatMap((item) => (
        (item.line_level_discount_allocations || [])
          .map((allocation) => allocation?.discount_application)
          .filter(Boolean)
      ));

      return [...new Set([...cartApplications, ...lineApplications]
        .filter((application) => application?.type === 'discount_code' && application.title)
        .map((application) => String(application.title))
      )];
    }

    async getCart() {
      const response = await fetch(`${this.root()}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      return response.json();
    }

    async updateDiscount(cart, tier) {
      const tierCodes = new Set([this.codeTwo, this.codeThree]);
      const currentCodes = this.manualCodes(cart);
      const otherCodes = currentCodes.filter((code) => !tierCodes.has(code));
      const currentTierCode = currentCodes.find((code) => tierCodes.has(code)) || '';

      if (currentTierCode === tier.code) return { cart, changed: false };

      const discountCodes = tier.code ? [...otherCodes, tier.code] : otherCodes;
      const response = await fetch(`${this.root()}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: discountCodes.join(',') })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Discount update failed: ${response.status}`);
      }
      return { cart: data, changed: true };
    }

    async refreshCartDisplay() {
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refreshCartItems === 'function') {
        await cartItems.refreshCartItems();
        return true;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.refreshCartDrawer === 'function') {
        await drawer.refreshCartDrawer(true);
        return true;
      }

      return false;
    }

    statusMessage(tier) {
      if (!tier?.code) return '';
      const template = tier.count >= 3 ? this.statusThree : this.statusTwo;
      return template
        .replaceAll('__CODE__', tier.code)
        .replaceAll('__PERCENT__', String(tier.percent));
    }

    renderStatus(result = this.lastResult) {
      const message = result?.applied ? this.statusMessage(result) : '';
      document.querySelectorAll('[data-kex-tiered-discount-status]').forEach((element) => {
        element.textContent = message;
        element.hidden = !message;
      });
    }

    emit(result) {
      this.lastResult = result;
      this.renderStatus(result);
      document.dispatchEvent(new CustomEvent('kex:tiered-discount:change', {
        detail: result
      }));
      return result;
    }

    async syncNow() {
      if (!this.isActive()) {
        this.renderStatus(null);
        return this.emit({ applied: false, code: '', percent: 0, count: 0 });
      }

      const cart = await this.getCart();
      const count = this.distinctProductCount(cart);
      const tier = this.tierForCount(count);
      const update = await this.updateDiscount(cart, tier);
      const applied = Boolean(tier.code && this.manualCodes(update.cart).includes(tier.code));
      let displayRefreshed = false;

      if (!tier.code) this.setActive(false);
      if (update.changed) displayRefreshed = await this.refreshCartDisplay();

      return this.emit({
        ...tier,
        applied,
        cart: update.cart,
        displayRefreshed
      });
    }

    schedule() {
      if (!this.isActive()) return Promise.resolve(this.lastResult);
      this.queue = this.queue
        .catch(() => null)
        .then(() => this.syncNow())
        .catch((error) => this.emit({
          applied: false,
          code: '',
          percent: 0,
          count: 0,
          error: error.message
        }));
      return this.queue;
    }

    activate() {
      this.setActive(true);
      return this.schedule();
    }
  }

  const managerScript = document.querySelector('script[data-kex-tiered-discount]');
  window.KexTieredDiscount = new KexTieredDiscountManager(managerScript);
}
